import React, { forwardRef, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Draggable, type DraggableChildrenFn, Droppable } from "react-beautiful-dnd";

import { faCheckDouble, faInbox, faTrashCan } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faSparkles } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { faChevronDown, faPlus } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";

import ActionItem from "~/actionItems/ActionItem";
import ActionItemGroupOptions from "~/actionItems/actionItemGroup/ActionItemGroupOptions";
import {
	ACTION_ITEMS_STORE,
	ADDITIONAL_ITEMS_TO_RENDER_ON_EXPANSION,
	GROUP_BY_ASSIGNEE,
	GROUP_BY_CUSTOM,
	GROUP_BY_DUE_DATE,
	GROUP_BY_NONE,
	GROUP_BY_STREAM,
	INITIAL_ITEMS_TO_RENDER,
	ITEM_DROPPABLE_TYPE,
	NO_DUE_DATE,
	OVERDUE,
	SORT_BY_CUSTOM,
	Views,
} from "~/actionItems/constants";
import { ActionItemsContext } from "~/actionItems/newActionItems/ActionItemsContext";
import type { ActionItem as ActionItemType, GroupType } from "~/actionItems/types";
import store from "~/lib/store";
import type { NodesFromEdges } from "~/lib/utils";
import { Button } from "~/ui";
import Avatar from "~/ui/legacy/Avatar";
import type { UserAttrs } from "~/ui/legacy/Avatar";
import { ButtonLink, IconButton } from "~/ui/legacyFellowIndex";

import { acceptAIActionItems, dismissAIActionItems } from "../AcceptDismissPopover";
import { AI_SUGGESTIONS_MAX_ITEMS, isGroupSyntheticAISuggestions } from "./aiSuggestionsUtils";

import type { ActionItemGroupsListQuery$data } from "~/__generated__/ActionItemGroupsListQuery.graphql";

import styles from "./ActionItemGroup.module.css";

export interface ActionItemGroupProps extends Omit<React.HTMLProps<HTMLDivElement>, "ref"> {
	group: GroupType;
	adjacentItemSelect(item: ActionItemType): void;
}

const actionItemsStore = store.namespace(ACTION_ITEMS_STORE);
const getCollapseStoreKey = (id: string) => `actionItemGroupCollapsed.${id}`;

function getDraggableInnerRenderer(
	items: NodesFromEdges<ActionItemGroupsListQuery$data["actionItemGroups"]>["items"],
	adjacentItemSelect: (item: ActionItemType) => void,
	inAISuggestions?: boolean,
): DraggableChildrenFn {
	return function DraggableGroupClone(provided, snapshot, rubric) {
		const item = items.edges[rubric.source.index];
		if (!item) {
			return <></>;
		}
		return (
			<ActionItem
				item={item.node}
				adjacentItemSelect={adjacentItemSelect}
				{...provided.draggableProps}
				{...provided.dragHandleProps}
				ref={provided.innerRef}
				inAISuggestions={inAISuggestions}
			/>
		);
	};
}

const ActionItemGroup = forwardRef<HTMLDivElement, ActionItemGroupProps>(function ActionItemGroup(
	{ group, adjacentItemSelect, ...rest },
	ref,
) {
	const [collapsed, setCollapsed] = actionItemsStore.useState<boolean>(getCollapseStoreKey(group.id), false);
	const [expansions, setExpansions] = useState<number>(0);
	const {
		filters,
		itemBeingEdited,
		onCreateActionItem,
		setGroupsState,
		setSelectedActionItems,
		hasAccessToDueDates,
		view,
		inPanel,
		viewer,
	} = useContext(ActionItemsContext);
	const user = viewer?.user;
	const { groupBy, sortBy } = filters;

	useEffect(() => {
		if (!setGroupsState) return;
		setGroupsState(groupsState => ({
			...groupsState,
			[group.id]: { collapsed, expansions },
		}));
	}, [group.id, collapsed, expansions, setGroupsState]);

	const toggleCollapsed = useCallback(() => {
		setCollapsed(collapsed => !collapsed);
		setSelectedActionItems?.([]);
	}, [setCollapsed, setSelectedActionItems]);
	const onExpand = useCallback(() => setExpansions(expansions => expansions + 1), []);

	const onClickAddButton = useCallback(() => {
		let groupId;
		let dueDate;
		let assignee: UserAttrs | undefined = user;
		if (groupBy === GROUP_BY_CUSTOM) {
			groupId = group.id;
		}
		if (groupBy === GROUP_BY_DUE_DATE && group.id !== NO_DUE_DATE && group.id !== OVERDUE) {
			dueDate = group.id;
		}
		if (groupBy === GROUP_BY_ASSIGNEE) {
			assignee = group.assignee;
		}
		onCreateActionItem(groupId, dueDate, assignee);
	}, [group, groupBy, user, onCreateActionItem]);

	const inRecording = view === Views.RECORDING;
	const isDropDisabled = useMemo(
		() =>
			inRecording ||
			groupBy === GROUP_BY_STREAM ||
			groupBy === GROUP_BY_ASSIGNEE ||
			(groupBy === GROUP_BY_NONE && sortBy !== SORT_BY_CUSTOM) ||
			(groupBy === GROUP_BY_DUE_DATE && (!hasAccessToDueDates || view === Views.TEAMMATES)) ||
			isGroupSyntheticAISuggestions(group),
		[inRecording, groupBy, sortBy, hasAccessToDueDates, view, group],
	);

	const amountDesiredToRender = useMemo(
		() =>
			isGroupSyntheticAISuggestions(group)
				? AI_SUGGESTIONS_MAX_ITEMS + expansions * AI_SUGGESTIONS_MAX_ITEMS
				: INITIAL_ITEMS_TO_RENDER + expansions * ADDITIONAL_ITEMS_TO_RENDER_ON_EXPANSION,
		[expansions, group],
	);
	const itemsToRender = useMemo(
		() =>
			group.items.edges.length < amountDesiredToRender
				? group.items.edges
				: group.items.edges.slice(0, amountDesiredToRender),
		[group, amountDesiredToRender],
	);

	const draggableInnerRenderer = useMemo(
		() => getDraggableInnerRenderer(group.items, adjacentItemSelect, isGroupSyntheticAISuggestions(group)),
		[group, adjacentItemSelect],
	);

	const enablePlusButton = useMemo(
		() =>
			!inRecording &&
			view !== Views.STREAM_TAB &&
			groupBy !== GROUP_BY_STREAM &&
			(groupBy !== GROUP_BY_DUE_DATE || hasAccessToDueDates || group.id === NO_DUE_DATE) &&
			!isGroupSyntheticAISuggestions(group),
		[inRecording, view, groupBy, hasAccessToDueDates, group],
	);

	const customGroupIcon = group.isDefaultGroup ? (
		<FontAwesomeIcon icon={faInbox} className={styles.groupIcon} />
	) : isGroupSyntheticAISuggestions(group) ? (
		<FontAwesomeIcon icon={faSparkles} className={cx(styles.groupIcon, styles.aiSuggestionsIcon)} />
	) : null;

	const acceptAllAISuggestions = useCallback(() => {
		if (!isGroupSyntheticAISuggestions(group)) {
			return;
		}
		const idsToAccept = group.items.edges
			.filter((edge): edge is NonNullable<typeof edge> => edge !== null)
			.map(edge => {
				return { id: edge.node.blockId };
			});
		acceptAIActionItems(idsToAccept);
	}, [group]);

	const dismissAllAISuggestions = useCallback(() => {
		if (!isGroupSyntheticAISuggestions(group)) {
			return;
		}
		const idsToAccept = group.items.edges
			.filter((edge): edge is NonNullable<typeof edge> => edge !== null)
			.map(edge => {
				return { id: edge.node.blockId };
			});
		dismissAIActionItems(idsToAccept);
	}, [group]);

	return (
		<div
			className={cx(styles.group, {
				[styles.collapsed]: collapsed,
				[styles.aiSuggestions]: isGroupSyntheticAISuggestions(group),
				[styles.inPanel]: inPanel,
			})}
			ref={ref}
			{...rest}
			data-is-action-item-group
		>
			{!inRecording && (
				<div className={cx(styles.groupHeader, { [styles.groupHeaderInPanel]: inPanel })}>
					<div className={styles.groupHeaderText}>
						<button
							className={cx(styles.collapseButton, { [styles.iconButton]: customGroupIcon || !!group.assignee })}
							onClick={toggleCollapsed}
						>
							{group.assignee && <Avatar user={group.assignee} link={false} className={styles.assignee} />}
							{customGroupIcon}
							<FontAwesomeIcon icon={faChevronDown} className={styles.collapseButtonCaret} />
							<p title={group.name}>{group.name}</p>
							{group.assignee && !group.assignee.isActive && <p>&nbsp;(inactive)</p>}
						</button>
						<span
							className={cx(styles.itemsAmount, { [styles.itemsAmountInPanel]: inPanel })}
							title={group.items.edges.length.toString()}
						>
							{group.items.edges.length}
						</span>
						{groupBy === GROUP_BY_CUSTOM && !isGroupSyntheticAISuggestions(group) && (
							<ActionItemGroupOptions group={group} />
						)}
					</div>
					{enablePlusButton && <IconButton icon={faPlus} onClick={onClickAddButton} className={styles.plusButton} />}
				</div>
			)}
			<Droppable
				type={ITEM_DROPPABLE_TYPE}
				droppableId={group.id}
				renderClone={draggableInnerRenderer}
				isDropDisabled={isDropDisabled}
			>
				{provided => (
					<div
						{...provided.droppableProps}
						ref={provided.innerRef}
						className={cx(styles.groupActionItems, {
							[styles.groupActionItemsInPanel]: inPanel,
							[styles.groupActionItemsInRecording]: inRecording,
							[styles.aiSuggestionsGroupItems]: isGroupSyntheticAISuggestions(group),
						})}
					>
						{itemsToRender.map((item, index) => {
							if (!item) {
								return null;
							}
							return (
								<Draggable
									key={item.node.id}
									draggableId={item.node.id}
									index={index}
									isDragDisabled={isDropDisabled || item.node.id === itemBeingEdited?.id}
									disableInteractiveElementBlocking // because our main drag handle area is interactable
								>
									{draggableInnerRenderer}
								</Draggable>
							);
						})}
						{provided.placeholder}
						{group.items.edges.length === 0 && (
							<p
								className={cx(styles.emptyState, {
									[styles.emptyStateInPanel]: inPanel,
									[styles.emptyStateInRecording]: inRecording,
								})}
							>
								{inRecording && <span>No action items were detected in this meeting.</span>}
								{!inRecording && group.assignee && (
									<span>
										{group.assignee.fullName} has no action items visible to you.{" "}
										<Button size="wide" variant="tertiary" onClick={onClickAddButton}>
											Add and assign an action item to {group.assignee.fullName}.
										</Button>
									</span>
								)}
								{!inRecording && !group.assignee && (
									<span>
										Group is empty. <ButtonLink onClick={onClickAddButton}>Create an action item</ButtonLink> or drag
										one into this group.
									</span>
								)}
							</p>
						)}
						{group.items.edges.length > amountDesiredToRender && (
							<Button
								size="wide"
								variant="tertiary"
								className={cx(styles.loadMoreButton, {
									[styles.loadMoreAISuggestions]: isGroupSyntheticAISuggestions(group),
								})}
								onClick={onExpand}
							>
								Show (
								{itemsToRender.length + ADDITIONAL_ITEMS_TO_RENDER_ON_EXPANSION > group.items.edges.length
									? group.items.edges.length - itemsToRender.length
									: ADDITIONAL_ITEMS_TO_RENDER_ON_EXPANSION}
								) More <FontAwesomeIcon icon={faChevronDown} fixedWidth />
							</Button>
						)}
						{isGroupSyntheticAISuggestions(group) && (
							<div className={cx(styles.aiBulkActions, { [styles.bulkActionsInPanel]: inPanel })}>
								<Button
									size="slim"
									variant="tertiary"
									className={styles.acceptAllButton}
									onClick={acceptAllAISuggestions}
								>
									<FontAwesomeIcon icon={faCheckDouble} /> {inPanel ? "Accept all" : "Accept all suggestions"}
								</Button>
								<Button size="slim" variant="tertiary" onClick={dismissAllAISuggestions}>
									<FontAwesomeIcon icon={faTrashCan} /> Dismiss all
								</Button>
							</div>
						)}
					</div>
				)}
			</Droppable>
		</div>
	);
});

export default ActionItemGroup;
